import {
  getCheckoutSessionFlags,
  getCheckoutSessionType,
  getLocale,
  getUnifiedLoginContextData,
} from '../globals';
import { hasTreatment, includesTreatment } from './utils';
import { isBillingAgreementWithoutPurchaseFlow } from '../utils/flow';
import { TREATMENTS } from './constants';
import { FPTI } from './fpti';
import { CountryCodes } from '@/generated/graphql';

export const getForcedGuestEligibility = (): boolean => {
  const { hasExistingAccount: hasExistingMemberAccount } =
    getUnifiedLoginContextData();
  const isExistingAccountBased = includesTreatment({
    feature: 'FEATURE_MERCHANT_BASED_FLOW_TYPE',
    treatment: 'EXISTING_MEMBER_ACCOUNT_BASED',
  });

  return Boolean(isExistingAccountBased && hasExistingMemberAccount);
};

export const getAccountBasedForcedSignupEligibility = (): boolean => {
  const { hasExistingAccount: hasExistingMemberAccount } =
    getUnifiedLoginContextData();
  const isExistingAccountBased = includesTreatment({
    feature: 'FEATURE_MERCHANT_BASED_FLOW_TYPE',
    treatment: 'EXISTING_MEMBER_ACCOUNT_BASED',
  });

  return Boolean(isExistingAccountBased && !hasExistingMemberAccount);
};

export const getPasswordlessForcedSignupEligibility = (): boolean => {
  const isShiftExpWithPasswordlessSignup = hasTreatment(
    'FEATURE_SHIFT_GUEST_TO_SIGNUP_PWORDLESS',
  );

  const isStreamlinePasswordlessExp = includesTreatment({
    feature: 'FEATURE_FORCED_SIGNUP_UI_EXP',
    treatment: 'STREAMLINE_WITHOUT_PASSWORD',
  });

  return (
    Boolean(isShiftExpWithPasswordlessSignup) || isStreamlinePasswordlessExp
  );
};

export const passwordlessForcedSignupEligibility = (): boolean => {
  const { isBillingAgreement } = getCheckoutSessionFlags();
  const isPasswordless = getPasswordlessForcedSignupEligibility();
  const isForcedGuest = getForcedGuestEligibility();

  return !isBillingAgreement && !isForcedGuest && isPasswordless;
};

export const passwordlessOptionalSignupEligibility = (): boolean => {
  // TODO: elmo experiment will need to be updated following review
  const isStreamlineExpWithPasswordlessSignup = includesTreatment({
    feature: 'FEATURE_OPTIONAL_SIGNUP_UI_EXP',
    treatment: 'STREAMLINE_WITHOUT_PASSWORD',
  });

  const isForcedGuest = getForcedGuestEligibility();

  return isStreamlineExpWithPasswordlessSignup && !isForcedGuest;
};

/**
 * @description - these are ineligible features for common WPS flows, such as,
 *    - Guest upgrade with password
 *    - Guest upgrade passwordless
 *    - Member matching benefits
 *    - WPS POMA
 *
 * @returns {Array} - list of ineligible features
 */
export const getCommonWpsGuestUpgradeIneligibilities = (): {
  ineligible: boolean;
  reason: string;
}[] => {
  const {
    isGuestEligible,
    isWPS,
    isWpsSubscription,
    isWpsAutobilling,
    isWpsInstallmentPlan,
  } = getCheckoutSessionFlags();

  const isSCAPhoneConfirmation = hasTreatment(
    TREATMENTS.FEATURE_SIGNUP_MANDATORY_PHONE_CONFIRMATION,
  );

  const isCRSEnabled = hasTreatment(TREATMENTS.FEATURE_CRS_DATA_COLLECTION);
  /*
    !!! PAY CLOSE ATTENTION WHEN EDITING INELIGIBLE CHECKS
    TO THIS ARRAY. ADDING A NEW INELIGIBILITY REQUIRES
    TESTING useCheckMemberBenefits HOOK AS WE CALL
    hasCommonWpsGuestUpgradeIneligibility INSIDE OF IT.
  */
  const ineligibleFeatures = [
    { ineligible: !isWPS, reason: 'isNotWps' },
    { ineligible: isCRSEnabled, reason: 'isCRSEnabled' },
    { ineligible: Boolean(isWpsSubscription), reason: 'isWpsSubscription' },
    { ineligible: Boolean(isWpsAutobilling), reason: 'isWpsAutobilling' },
    {
      ineligible: Boolean(isWpsInstallmentPlan),
      reason: 'isWpsInstallmentPlan',
    },
    { ineligible: !isGuestEligible, reason: 'isNotGuestEligible' },
    { ineligible: isSCAPhoneConfirmation, reason: 'isSCAPhoneConfirmation' },
  ];

  return ineligibleFeatures;
};

/**
 * @returns {bool}
 */
export const hasCommonWpsGuestUpgradeIneligibility = (): boolean => {
  const commonWpsIneligibilities = getCommonWpsGuestUpgradeIneligibilities();
  return commonWpsIneligibilities.some(({ ineligible }) => ineligible);
};

/**
 * @description - Checks the checkout session and ELMO treatments to
 * determine if the current user is eligible for member matching on WPS
 * @returns {bool} - Whether a user is eligible for WPS member match
 */
export const getWpsMemberMatchEligiblity = (): boolean => {
  const { isWPS, isWpsSubscription, isWpsAutobilling, isWpsInstallmentPlan } =
    getCheckoutSessionFlags();

  const memberMatchEnabled = hasTreatment(
    TREATMENTS.FEATURE_EXPAND_MEMBER_MATCHING_WPS,
  );
  /*
    The following conditions must evaluate to true
    to be eligible for member match on WPS
  */
  const requirements = [
    isWPS,
    memberMatchEnabled,
    !isWpsSubscription,
    !isWpsAutobilling,
    !isWpsInstallmentPlan,
  ];

  return requirements.every(requirement => requirement);
};

/**
 * @returns {bool}
 */
export const getWpsGuestUpgradeEligibility = (): boolean => {
  const { country } = getLocale();
  // Guest Upgrade flow should be ineligible only for POMA phase 1
  const isWpsPOMATreatment = includesTreatment({
    feature: TREATMENTS.FEATURE_WPS_PASSWORDLESS,
    treatment: 'OPTIONAL_GUEST_WITHOUT_PASSWORD',
  });
  const isStreamlineOptional = getStreamlineOptionalEligibility();

  const commonWpsIneligibilities = [
    ...getCommonWpsGuestUpgradeIneligibilities(),
    { ineligible: isWpsPOMATreatment, reason: 'isWpsPOMATreatment' },
    /*
      !!! THIS SUPPRESSES GUEST UPGRADE FOR STREAMLINE EXP AND
      THIS IS THE PATH FORWARD BASED ON PRODUCT DISCUSSIONS.
    */
    { ineligible: isStreamlineOptional, reason: 'isStreamlineOptional' },
    { ineligible: country === CountryCodes.Th, reason: 'thailand' },
    { ineligible: country === CountryCodes.Gi, reason: 'gibraltar' },
  ];

  const ineligibleFeature = commonWpsIneligibilities.find(
    ({ ineligible }) => ineligible,
  );

  if (ineligibleFeature) {
    const { reason } = ineligibleFeature;
    FPTI.isWpsGuestUpgradeEligible.logEligibility(`ineligible_${reason}`);
    return false;
  }

  FPTI.isWpsGuestUpgradeEligible.logEligibility('eligible');
  return true;
};

/**
 * Encapsulates ONLY the experiments filters to determine if
 * the POMA experiences(EC OR WPS) should be enabled based on elmo.
 *
 * @returns {bool}
 */
export const isPOMAEnabled = (): boolean => {
  const { country } = getLocale();
  const { isGuestEligible, isWPS } = getCheckoutSessionFlags();

  // POMA EC
  const isPOMAEC = Boolean(
    country === CountryCodes.Us && isGuestEligible && !isWPS,
  );
  // POMA WPS
  const isPOMAWps = Boolean(
    !hasCommonWpsGuestUpgradeIneligibility() &&
      includesTreatment({
        feature: TREATMENTS.FEATURE_WPS_PASSWORDLESS,
        treatment: 'OPTIONAL_GUEST_WITHOUT_PASSWORD',
      }),
  );

  return isPOMAEC || isPOMAWps;
};

/**
 * Encapsulates ALL of the eligibility criterias to
 * determine if the POMA experiences(EC OR WPS) should be shown to the user.
 *
 * @returns {bool}
 */
export const getPOMAEligibility = (): boolean => {
  const { isInvoiceFlow } = getCheckoutSessionFlags();
  const checkoutSessionType = getCheckoutSessionType();
  const isBAWithoutPurchase =
    isBillingAgreementWithoutPurchaseFlow(checkoutSessionType);
  // RPS elmo experiment takes priority over FLOW_TYPE elmo
  const isRPSTreatment = hasTreatment('FEATURE_MERCHANT_BASED_FLOW_TYPE');

  // only available to US, POMA is default treated as control.
  // POMA eligible customers given the streamline treatment
  // should be sent down optional or forced signup
  const isStreamlineExperienceEligible = getStreamlineOptionalEligibility();

  return (
    !isInvoiceFlow &&
    !isBAWithoutPurchase &&
    !isRPSTreatment &&
    !isStreamlineExperienceEligible &&
    isPOMAEnabled()
  );
};

export const getStreamlineForcedEligibility = (): boolean => {
  const isStreamlineExperience = includesTreatment({
    feature: 'FEATURE_FORCED_SIGNUP_UI_EXP',
    treatment: 'FORCED_SIGNUP_STREAMLINE',
  });

  const isPOMAEligible = getPOMAEligibility();
  const isForcedGuest = getForcedGuestEligibility();

  return isStreamlineExperience && !isPOMAEligible && !isForcedGuest;
};

export const getStreamlineOptionalEligibility = (): boolean => {
  const { isGuestEligible } = getCheckoutSessionFlags();
  const isStreamlineExperience = includesTreatment({
    feature: 'FEATURE_OPTIONAL_SIGNUP_UI_EXP',
    treatment: 'OPTIONAL_SIGNUP_STREAMLINE',
  });
  const isForcedGuest = getForcedGuestEligibility();
  const isForcedSignup = includesTreatment({
    feature: 'FEATURE_MERCHANT_BASED_FLOW_TYPE',
    treatment: 'FORCED_SIGNUP',
  });
  const isAccountBasedForcedSignup = getAccountBasedForcedSignupEligibility();

  return (
    Boolean(isGuestEligible) &&
    isStreamlineExperience &&
    !isForcedGuest &&
    !isForcedSignup &&
    !isAccountBasedForcedSignup
  );
};

export const getPasswordlessUpgradeEligibility = (): boolean => {
  const isPasswordless = includesTreatment({
    feature: 'FEATURE_WPS_PASSWORDLESS',
    treatment: 'WPS_PASSWORDLESS_2',
  });

  return !hasCommonWpsGuestUpgradeIneligibility() && isPasswordless;
};

export const getMemberMatchNoOptInEligibility = (): boolean => {
  const isECMemberMatch = includesTreatment({
    feature: 'FEATURE_EXPAND_MEMBER_MATCHING',
    treatment: 'EXPAND_MEMBER_MATCHING_2',
  });
  const isWPSMemberMatch = includesTreatment({
    feature: 'FEATURE_EXPAND_MEMBER_MATCHING_WPS',
    treatment: 'MEMBER_MATCHING_WPS_NO_OPT_IN',
  });

  const optionalStreamlineSignupEligible = getStreamlineOptionalEligibility();
  const signupRequired = getStreamlineForcedEligibility();
  return (
    (isECMemberMatch || isWPSMemberMatch) &&
    (optionalStreamlineSignupEligible || signupRequired)
  );
};

export const getPPReactMockStylesEligibility = (): boolean => {
  const isPPReactStylesEligible = hasTreatment(
    TREATMENTS.FEATURE_PP_REACT_MOCK_STYLES,
  );
  return Boolean(isPPReactStylesEligible);
};
